import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon, XCircleIcon, ClockIcon,
  ArrowPathIcon, FingerPrintIcon, PencilSquareIcon,
  XMarkIcon, PlusCircleIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ta * { box-sizing:border-box; }
  .ta { font-family:'Sora',sans-serif; color:#1a1d2e; animation:taFade .4s ease both; }
  @keyframes taFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ta-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .ta-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ta-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }

  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover { background:#2d5ef0; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-amber { background:#fffbeb; color:#f59e0b; border:1px solid #fde68a; }
  .btn-amber:hover { background:#f59e0b; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }

  .machine-banner { border-radius:12px; padding:10px 16px; margin-bottom:1.2rem; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px; }
  .machine-banner.on  { background:#ecfdf5; border:1px solid #a7f3d0; }
  .machine-banner.off { background:#fef2f2; border:1px solid #fecaca; }
  .mb-left { display:flex; align-items:center; gap:10px; flex:1; min-width:0; }
  .mb-dot  { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
  .mb-dot.on  { background:#10c98e; animation:mbPulse 2s infinite; }
  .mb-dot.off { background:#ef4444; }
  @keyframes mbPulse { 0%,100%{opacity:1} 50%{opacity:.4} }
  .mb-text { font-size:.8rem; font-weight:600; color:#0f1123; }
  .mb-sub  { font-size:.7rem; color:#6b7194; }

  .summary-row { display:flex; gap:8px; margin-bottom:1.2rem; flex-wrap:wrap; }
  .sum-chip { padding:6px 14px; border-radius:99px; font-size:.76rem; font-weight:600; white-space:nowrap; }
  .sum-chip.present  { background:#ecfdf5; color:#10c98e; }
  .sum-chip.absent   { background:#fef2f2; color:#ef4444; }
  .sum-chip.checkedin{ background:#eef2ff; color:#3b6ef8; }
  .sum-chip.total    { background:#f0f2f7; color:#6b7194; }

  .filter-row { display:flex; gap:10px; margin-bottom:1.2rem; align-items:flex-end; flex-wrap:wrap; }
  .filter-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; display:block; margin-bottom:5px; }
  .filter-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; color:#1a1d2e; outline:none; }
  .filter-input:focus { border-color:#3b6ef8; }

  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }

  /* Desktop table */
  .att-table { width:100%; border-collapse:collapse; }
  .att-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .att-table th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .att-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .att-table tbody tr:last-child { border-bottom:none; }
  .att-table tbody tr:hover { background:#fafbff; }
  .att-table td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }

  /* Mobile teacher attendance cards */
  .att-cards { display:none; padding:10px; }
  .att-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .ac-top    { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .ac-row    { display:flex; align-items:center; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .ac-row:last-of-type { border-bottom:none; }
  .ac-label  { color:#9094a8; font-weight:600; white-space:nowrap; }
  .ac-actions{ display:flex; gap:5px; margin-top:8px; flex-wrap:wrap; }
  .ac-actions .btn { flex:1; justify-content:center; }

  .t-avatar { width:32px; height:32px; border-radius:9px; background:#ecfdf5; color:#10c98e; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; flex-shrink:0; }
  .t-name   { font-size:.82rem; font-weight:600; color:#0f1123; }
  .t-emp    { font-size:.7rem; color:#9094a8; font-family:'JetBrains Mono',monospace; margin-top:1px; }

  .status-badge { display:inline-flex; align-items:center; gap:5px; font-size:.72rem; font-weight:600; padding:4px 10px; border-radius:99px; white-space:nowrap; }
  .status-badge.present   { background:#ecfdf5; color:#10c98e; }
  .status-badge.absent    { background:#fef2f2; color:#ef4444; }
  .status-badge.checkedin { background:#eef2ff; color:#3b6ef8; }
  .status-badge.on-leave  { background:#f3f0ff; color:#8b5cf6; }
  .status-badge.holiday   { background:#f0f2f7; color:#6b7194; }
  .status-badge svg { width:12px; height:12px; }

  .time-cell       { font-family:'JetBrains Mono',monospace; font-size:.78rem; color:#1a1d2e; font-weight:500; }
  .time-cell.empty { color:#d1d5db; }
  .biometric-tag { display:inline-flex; align-items:center; gap:4px; font-size:.65rem; font-weight:600; padding:2px 7px; border-radius:99px; background:#f3f0ff; color:#8b5cf6; }
  .biometric-tag svg { width:10px; height:10px; }
  .manual-tag { display:inline-flex; font-size:.65rem; font-weight:600; padding:2px 7px; border-radius:99px; background:#f0f2f7; color:#6b7194; }

  .ta-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .ta-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:flex-start; justify-content:center; z-index:1000; padding:24px 16px; overflow-y:auto; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:460px; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; margin:auto; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; flex-wrap:wrap; }
  .form-grid  { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.span2 { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus { border-color:#3b6ef8; background:white; }

  /* ── Mobile ── */
  @media (max-width:768px) {
    .ta-title { font-size:1.1rem; }

    /* Filters */
    .filter-row { flex-direction:column; gap:8px; }
    .filter-input { width:100%; }

    /* Hide table, show cards */
    .att-table-wrap { display:none !important; }
    .att-cards { display:block; }

    /* Modals */
    .modal { padding:18px; }
    .form-grid { grid-template-columns:1fr; }
    .form-field.span2 { grid-column:1; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }
`;

const todayStr = ()=>{const d=new Date();return`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;};
const fmtTime  = (d)=>d?new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}):null;
const initials = (n='')=>n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';

const TeacherAttendancePage = () => {
  const [teachers,     setTeachers]     = useState([]);
  const [attendance,   setAttendance]   = useState({});
  const [loading,      setLoading]      = useState(true);
  const [date,         setDate]         = useState(todayStr());
  const [machineStatus,setMachineStatus]= useState(null);
  const [processing,   setProcessing]   = useState({});
  const [editModal,    setEditModal]    = useState(null);
  const [addModal,     setAddModal]     = useState(null);
  const [editForm,     setEditForm]     = useState({});
  const [addForm,      setAddForm]      = useState({checkIn:'',checkOut:'',status:'present',lateMinutes:'0',earlyExitMinutes:'0'});
  const [saving,       setSaving]       = useState(false);
  const isToday=date===todayStr();

  const fetchAll=useCallback(async(signal)=>{
    try{
      setLoading(true);
      const{data:sal}=await api.get('/admin/salary/teachers',{signal});
      setTeachers(sal||[]);
      const{data:att}=await api.get(`/admin/attendance/overview?startDate=${date}&endDate=${date}`,{signal});
      const map={};
      (att||[]).forEach(r=>{map[r.user?._id?.toString()]=r;});
      setAttendance(map);
    }catch(e){if(e.code==='ERR_CANCELED')return;}
    finally{setLoading(false);}
  },[date]);

  useEffect(()=>{
    const c=new AbortController();fetchAll(c.signal);return()=>c.abort();
  },[fetchAll]);

  useEffect(()=>{
    api.get('/admin/biometric/status').then(r=>setMachineStatus(r.data)).catch(()=>{});
    if(isToday){
      const interval=setInterval(()=>{
        fetchAll(new AbortController().signal);
        api.get('/admin/biometric/status').then(r=>setMachineStatus(r.data)).catch(()=>{});
      },30000);
      return()=>clearInterval(interval);
    }
  },[isToday]);

  const handleCheckIn=async(userId)=>{
    setProcessing(p=>({...p,[userId]:true}));
    try{await api.post('/admin/attendance/teacher/checkin',{teacherId:userId});toast.success('Checked in');fetchAll(new AbortController().signal);}
    catch(e){toast.error(e.response?.data?.message||'Failed');}
    finally{setProcessing(p=>({...p,[userId]:false}));}
  };

  const handleCheckOut=async(userId)=>{
    setProcessing(p=>({...p,[userId]:true}));
    try{await api.post('/admin/attendance/teacher/checkout',{teacherId:userId});toast.success('Checked out');fetchAll(new AbortController().signal);}
    catch(e){toast.error(e.response?.data?.message||'Failed');}
    finally{setProcessing(p=>({...p,[userId]:false}));}
  };

  const fmtTime24=(d)=>{if(!d)return'';const t=new Date(d);return`${String(t.getHours()).padStart(2,'0')}:${String(t.getMinutes()).padStart(2,'0')}`;};

  const openEdit=(record)=>{setEditModal(record);setEditForm({checkIn:fmtTime24(record.checkIn),checkOut:fmtTime24(record.checkOut),status:record.status||'present',lateMinutes:String(record.lateMinutes||0),earlyExitMinutes:String(record.earlyExitMinutes||0)});};

  const handleEdit=async()=>{
    setSaving(true);
    try{await api.put(`/admin/attendance/record/${editModal._id}`,editForm);toast.success('Record updated');setEditModal(null);fetchAll(new AbortController().signal);}
    catch(e){toast.error(e.response?.data?.message||'Failed');}finally{setSaving(false);}
  };

  const openAdd=(teacher)=>{setAddModal(teacher);setAddForm({checkIn:'09:00',checkOut:'',status:'present',lateMinutes:'0',earlyExitMinutes:'0'});};

  const handleAdd=async()=>{
    setSaving(true);
    try{
      await api.post('/admin/attendance/record',{teacherId:addModal.userId||addModal.teacherId,date,...addForm,lateMinutes:Number(addForm.lateMinutes),earlyExitMinutes:Number(addForm.earlyExitMinutes)});
      toast.success('Attendance record added');setAddModal(null);fetchAll(new AbortController().signal);
    }catch(e){toast.error(e.response?.data?.message||'Failed');}finally{setSaving(false);}
  };

  const getStatusLabel=(userId)=>{
    const r=attendance[userId];
    if(!r)return{label:'Not marked',cls:'absent'};
    if(r.checkIn&&!r.checkOut)return{label:'Checked In',cls:'checkedin'};
    if(r.checkIn&&r.checkOut) return{label:'Present',   cls:'present'};
    if(r.status==='on-leave') return{label:'On Leave',  cls:'on-leave'};
    return{label:r.status||'Absent',cls:r.status||'absent'};
  };

  const presentCount  =teachers.filter(t=>{const r=attendance[t.userId||t.teacherId];return r?.checkIn&&r?.checkOut;}).length;
  const checkedInCount=teachers.filter(t=>{const r=attendance[t.userId||t.teacherId];return r?.checkIn&&!r?.checkOut;}).length;
  const absentCount   =teachers.filter(t=>!attendance[t.userId||t.teacherId]).length;

  return (
    <div className="ta"><style>{style}</style>

      <div className="ta-header">
        <div><div className="ta-title">Teacher Attendance</div><div className="ta-sub">Manual check-in/out · Biometric auto-sync every 30s</div></div>
        <button className="btn btn-ghost btn-sm" onClick={()=>fetchAll(new AbortController().signal)}><ArrowPathIcon />Refresh</button>
      </div>

      {machineStatus&&(
        <div className={`machine-banner ${machineStatus.isPolling?'on':'off'}`}>
          <div className="mb-left">
            <div className={`mb-dot ${machineStatus.isPolling?'on':'off'}`}/>
            <div>
              <div className="mb-text">{machineStatus.isPolling?'🤖 Biometric machine active':'⚠️ Biometric machine not polling'}</div>
              <div className="mb-sub">{machineStatus.isPolling?`Syncing every ${machineStatus.config?.pollInterval||60}s`:'Go to Biometric settings to start'}</div>
            </div>
          </div>
          {machineStatus.isPolling&&<span style={{fontSize:'.72rem',color:'#10c98e',fontWeight:600}}>● Live</span>}
        </div>
      )}

      <div className="filter-row">
        <div>
          <label className="filter-label">Date</label>
          <input type="date" className="filter-input" value={date} onChange={e=>setDate(e.target.value)} />
        </div>
        {!isToday&&<button className="btn btn-ghost btn-sm" onClick={()=>setDate(todayStr())}>Today</button>}
      </div>

      <div className="summary-row">
        <span className="sum-chip total">Total: {teachers.length}</span>
        <span className="sum-chip present">Present: {presentCount}</span>
        <span className="sum-chip checkedin">In: {checkedInCount}</span>
        <span className="sum-chip absent">Not Marked: {absentCount}</span>
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="panel-title">{isToday?"Today's Attendance":`Attendance — ${new Date(date).toLocaleDateString('en-US',{weekday:'long',day:'numeric',month:'long'})}`}</div>
        </div>

        {loading ? (
          <div className="ta-loading"><div className="spinner"/>Loading…</div>
        ) : teachers.length===0 ? (
          <div className="ta-empty">No teachers found.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="att-table-wrap" style={{overflowX:'auto'}}>
              <table className="att-table">
                <thead><tr>
                  <th>Teacher</th><th>Status</th><th>Check In</th><th>Check Out</th>
                  <th>Work Hrs</th><th>Late Min</th><th>Source</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  {teachers.map(t=>{
                    const userId=t.userId||t.teacherId;
                    const record=attendance[userId];
                    const{label,cls}=getStatusLabel(userId);
                    const isProcessing=processing[userId];
                    const hasCheckIn=!!record?.checkIn;
                    const hasCheckOut=!!record?.checkOut;
                    const isBiometric=record?.source==='biometric';
                    return (
                      <tr key={userId}>
                        <td><div style={{display:'flex',alignItems:'center',gap:9}}><div className="t-avatar">{initials(t.name)}</div><div><div className="t-name">{t.name}</div><div className="t-emp">{t.employeeId||'—'}</div></div></div></td>
                        <td><span className={`status-badge ${cls}`}>{cls==='present'&&<CheckCircleIcon />}{cls==='absent'&&<XCircleIcon />}{cls==='checkedin'&&<ClockIcon />}{label}</span></td>
                        <td>{hasCheckIn?<span className="time-cell">{fmtTime(record.checkIn)}</span>:<span className="time-cell empty">—</span>}</td>
                        <td>{hasCheckOut?<span className="time-cell">{fmtTime(record.checkOut)}</span>:<span className="time-cell empty">—</span>}</td>
                        <td><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',fontWeight:600,color:record?.workHours>0?'#0f1123':'#d1d5db'}}>{record?.workHours?`${Number(record.workHours).toFixed(1)}h`:'—'}</span></td>
                        <td><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:record?.lateMinutes>0?'#f59e0b':'#d1d5db',fontWeight:record?.lateMinutes>0?700:400}}>{record?.lateMinutes>0?`${record.lateMinutes}m`:'—'}</span></td>
                        <td>{record?(isBiometric?<span className="biometric-tag"><FingerPrintIcon />Biometric</span>:<span className="manual-tag">Manual</span>):<span className="time-cell empty">—</span>}</td>
                        <td><div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                          {isToday&&!hasCheckIn&&<button className="btn btn-green btn-sm" onClick={()=>handleCheckIn(userId)} disabled={isProcessing}>{isProcessing?'…':'Check In'}</button>}
                          {isToday&&hasCheckIn&&!hasCheckOut&&<button className="btn btn-amber btn-sm" onClick={()=>handleCheckOut(userId)} disabled={isProcessing}>{isProcessing?'…':'Check Out'}</button>}
                          {record&&<button className="btn btn-ghost btn-sm" onClick={()=>openEdit(record)}><PencilSquareIcon />Edit</button>}
                          {!record&&<button className="btn btn-ghost btn-sm" onClick={()=>openAdd(t)}><PlusCircleIcon />Add</button>}
                        </div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="att-cards">
              {teachers.map(t=>{
                const userId=t.userId||t.teacherId;
                const record=attendance[userId];
                const{label,cls}=getStatusLabel(userId);
                const isProcessing=processing[userId];
                const hasCheckIn=!!record?.checkIn;
                const hasCheckOut=!!record?.checkOut;
                const isBiometric=record?.source==='biometric';
                return (
                  <div key={userId} className="att-card">
                    <div className="ac-top">
                      <div style={{display:'flex',alignItems:'center',gap:9}}>
                        <div className="t-avatar">{initials(t.name)}</div>
                        <div><div className="t-name">{t.name}</div><div className="t-emp">{t.employeeId||'—'}</div></div>
                      </div>
                      <span className={`status-badge ${cls}`}>{label}</span>
                    </div>
                    {hasCheckIn&&<div className="ac-row"><span className="ac-label">Check In</span><span className="time-cell">{fmtTime(record.checkIn)}</span></div>}
                    {hasCheckOut&&<div className="ac-row"><span className="ac-label">Check Out</span><span className="time-cell">{fmtTime(record.checkOut)}</span></div>}
                    {record?.workHours>0&&<div className="ac-row"><span className="ac-label">Hours</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',fontWeight:600}}>{Number(record.workHours).toFixed(1)}h</span></div>}
                    {record?.lateMinutes>0&&<div className="ac-row"><span className="ac-label">Late</span><span style={{color:'#f59e0b',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",fontSize:'.76rem'}}>{record.lateMinutes}m</span></div>}
                    {record&&<div className="ac-row"><span className="ac-label">Source</span>{isBiometric?<span className="biometric-tag"><FingerPrintIcon />Biometric</span>:<span className="manual-tag">Manual</span>}</div>}
                    <div className="ac-actions">
                      {isToday&&!hasCheckIn&&<button className="btn btn-green btn-sm" onClick={()=>handleCheckIn(userId)} disabled={isProcessing}>{isProcessing?'…':'Check In'}</button>}
                      {isToday&&hasCheckIn&&!hasCheckOut&&<button className="btn btn-amber btn-sm" onClick={()=>handleCheckOut(userId)} disabled={isProcessing}>{isProcessing?'…':'Check Out'}</button>}
                      {record&&<button className="btn btn-ghost btn-sm" onClick={()=>openEdit(record)}><PencilSquareIcon />Edit</button>}
                      {!record&&<button className="btn btn-ghost btn-sm" onClick={()=>openAdd(t)}><PlusCircleIcon />Add</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editModal&&createPortal(
        <div className="modal-backdrop" onClick={()=>setEditModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Edit Attendance Record</div><button className="modal-close" onClick={()=>setEditModal(null)}><XMarkIcon /></button></div>
            <div style={{fontSize:'.78rem',color:'#6b7194',marginBottom:16}}>{editModal.user?.name||'Teacher'} · {new Date(editModal.date).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'})}</div>
            <div className="form-grid">
              <div className="form-field"><label className="form-label">Check In</label><input type="time" className="form-input" value={editForm.checkIn} onChange={e=>setEditForm({...editForm,checkIn:e.target.value})} /></div>
              <div className="form-field"><label className="form-label">Check Out</label><input type="time" className="form-input" value={editForm.checkOut} onChange={e=>setEditForm({...editForm,checkOut:e.target.value})} /></div>
              <div className="form-field"><label className="form-label">Status</label>
                <select className="form-select" value={editForm.status} onChange={e=>setEditForm({...editForm,status:e.target.value})}>
                  <option value="present">Present</option><option value="absent">Absent</option>
                  <option value="on-leave">On Leave</option><option value="holiday">Holiday</option>
                </select>
              </div>
              <div className="form-field"><label className="form-label">Late Minutes</label><input type="number" className="form-input" min={0} value={editForm.lateMinutes} onChange={e=>setEditForm({...editForm,lateMinutes:e.target.value})} /></div>
              <div className="form-field span2"><label className="form-label">Early Exit Minutes</label><input type="number" className="form-input" min={0} value={editForm.earlyExitMinutes} onChange={e=>setEditForm({...editForm,earlyExitMinutes:e.target.value})} /></div>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setEditModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleEdit} disabled={saving}>{saving?'Saving…':'Save Changes'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* Add Modal */}
      {addModal&&createPortal(
        <div className="modal-backdrop" onClick={()=>setAddModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Add Attendance — {addModal.name}</div><button className="modal-close" onClick={()=>setAddModal(null)}><XMarkIcon /></button></div>
            <div style={{fontSize:'.78rem',color:'#6b7194',marginBottom:16}}>Manual entry for {new Date(date).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'})}</div>
            <div className="form-grid">
              <div className="form-field"><label className="form-label">Check In *</label><input type="time" className="form-input" value={addForm.checkIn} onChange={e=>setAddForm({...addForm,checkIn:e.target.value})} /></div>
              <div className="form-field"><label className="form-label">Check Out</label><input type="time" className="form-input" value={addForm.checkOut} onChange={e=>setAddForm({...addForm,checkOut:e.target.value})} /></div>
              <div className="form-field"><label className="form-label">Status</label>
                <select className="form-select" value={addForm.status} onChange={e=>setAddForm({...addForm,status:e.target.value})}>
                  <option value="present">Present</option><option value="absent">Absent</option>
                  <option value="on-leave">On Leave</option><option value="holiday">Holiday</option>
                </select>
              </div>
              <div className="form-field"><label className="form-label">Late Minutes</label><input type="number" className="form-input" min={0} value={addForm.lateMinutes} onChange={e=>setAddForm({...addForm,lateMinutes:e.target.value})} /></div>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setAddModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleAdd} disabled={saving}>{saving?'Adding…':'Add Record'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default TeacherAttendancePage;